import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Grid,
  Input,
  message,
  Modal,
  Radio,
  Row,
  Spin,
  Tooltip,
  Typography,
  Dropdown,
  Space,
} from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  EyeInvisibleOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  FileDoneOutlined,
  PushpinFilled,
  CheckCircleFilled,
  MinusCircleFilled,
  DownOutlined,
  PrinterFilled,
} from '@ant-design/icons';
import currencyFormater from 'utils/toCurrency';
import Previewer from 'components/Previewer';
import Select from 'components/Select';
import getFormatedValues, {
  DATE_FORMAT,
  DATE_NETPAY_FORMAT,
  formatReceived,
} from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { defSigob } from 'themes';
import GlobalStyles from 'themes/global';
import Footer from 'components/Layout/Footer';
import { getConfiguracionesDeUsosDeFolios } from 'api/configuracion/configuracionesDeUsoFolios';
import { getCatalogoDePadrones, getTipoDePadronPorContenType } from 'api/catalogos';
import { getMetodosDePago } from 'api/configuracion';
import { getBancos } from 'api/configuracion/bancos';
import {
  getServiciosDeVehiculo,
  getEstadosDeVehiculo,
  getMarcasDeVehiculos,
  getLineasDeVehiculos,
  getTiposDeVehiculos,
  getClasesDeVehiculos,
  getConsultarCargosCiudadanos,
  getConstancia,
  postConsultaFolio,
  putDescuentos,
} from 'api/recaudacion/index';
import { getTiposDeEstablecimientos } from 'api/empresas';
import {
  getCiudadanoCaja,
  postImprimirObligaciones,
  postImprimirOrdenDePago,
} from 'api/cuentaunicasir/index';
import { patchCiudadano } from 'api/cuentaunicasir/ciudadanos';
import { getRecibosPorPadrones, postRecibo } from 'api/recaudacion/recibos';
import { venta } from 'api/netpay';
import formatParams from 'utils/formatParams';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import useCorte from 'hooks/useCorte';
import ModalCorteControls from 'views/Recaudacion/Caja/ModalCorteControls';
import { getDepositosEnGarantia } from 'api/recaudacion/depositosEnGarantia';
import ModalAlertas from 'views/Recaudacion/Caja/ModalAlertas';
import InputSearch from 'views/Recaudacion/Caja/InputSearch';
import ModalInfoPadron from 'views/Recaudacion/Caja/ModalInfoPadron';
import ModalAgregarCargos from 'views/Recaudacion/Caja/ModalAgregarCargos';
import BusquedaAvanzada from 'views/Recaudacion/Caja/BusquedaAvanzada';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import DatosDePago from 'views/Recaudacion/Caja/DatosDePago';
import ModalObservaciones from 'views/Recaudacion/Caja/ModalObservaciones';
import PanelPadron, { getTotalPadron, getTotalSinRound } from 'views/Recaudacion/Caja/PanelPadron';
import {
  capitalize,
  decimalNormalizer,
  getDistinctValues,
  toMoment,
} from 'utils/normalizers';
import checkToken from 'api/utils/checkToken';
import {
  ADD_PADRON,
  SET_PADRONES,
  CLEAN_CAJA,
  SET_PADRON_REQUIERE_FETCH,
  SET_PIN_PADRON_AGREGAR_CARGOS,
  CLEAR_NETPAY_RESPONSE,
  SET_LAST_PAYMENT_DATA,
  ADD_PADRONES,
} from 'store/reducers/caja';
import { getPadronesMap, retrievePadronesMap } from 'api/recaudacion/utils';
import padronToString from 'utils/padronesToString';
import {
  hasAdvanceSearchPermissions,
  hasPadronPermission,
  permissionSectionsPadrones,
} from 'utils/permission/models';
import ModalPadronesAConsultarAdeudo from 'views/Recaudacion/Caja/ModalPadronesAConsultarAdeudo';
import { recalculateCargos, sortCargos } from 'utils/cargos';
import { postObtenerReporte } from 'api/reporteador';
import { ActionsNames, hasActionPermission } from 'utils/permission';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import ModalExtraInfoPaymentOrder from 'components/ModalExtraInfoPaymentOrder';
import usePermissionMessage from 'hooks/usePermissionMessage';
import { getFileName, TIPOS_DE_DOCUMENTO_ENUM } from 'utils/getFileName';
import round from 'utils/round';
import useConfigOmisionDeRecargos from 'hooks/useConfigOmisionDeRecargos';
import Calculadora from './CalculadoraDeMonto';
import ModalConvenio from './Convenios';
import ModalCobroParcialidades from './Convenios/ModalCobroParcialidades';
import ModalReciboConvenio from './Convenios/ModalReciboConvenio';
import ModalAcreditacion from '../components/ModalAcreditacion';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const PROD = process.env.REACT_APP_ENV === 'PRODUCTION';
const ENV_NAME = process.env.REACT_APP_ENV_NAME || 'migob';
const VENTANILLA = process.env.REACT_APP_ENV_ALTERN === 'ventanilla';
const IPROVINAY = ENV_NAME === 'iprovinay';

const required = [
  'view_ciudadano',
  'view_cargo',
  'view_contenttype',
  'view_padron',
  'view_configuraciondeclave',
];

const permisosCobrar = [
  'view_corte',
  'add_corte',
  'view_banco',
  'view_catalogodemetododepago',
  'add_recibo',
  'view_recibo',
  'view_catalogodeefectivo',
  'view_usodefolio',
  // Depositos en garantía??
];

const customActions = [
  {
    name: 'generar_cargos',
    title: 'Generar cargos',
    permissions: [
      'view_tipodecargo',
      'view_tipodeaplicacionparacargo',
      'view_variable',
      'view_valorporvariabledeseleccion',
      'add_cargo',
    ],
    ventanilla: true,
  },
  {
    name: 'cobrar',
    title: 'Cobrar',
    permissions: permisosCobrar,
  },
  {
    name: 'cobrar_publico_general',
    title: 'Cobrar público general',
    permissions: ['cobrar_publico_general', ...permisosCobrar],
    ventanilla: true,
  },
  {
    name: 'puede_ver_orden_catastro',
    title: 'Imprimir orden de Pago (Catastro)',
    permissions: ['puede_ver_orden_catastro'],
    ventanilla: true,
  },
  {
    name: 'generar_convenio',
    title: 'Generar convenio',
    permissions: [
      'add_convenio',
      'view_tipodeconvenio',
      'add_parcialidaddelconvenio',
      'view_recibo',
    ],
    ventanilla: true,
  },
  {
    name: 'cancelar_convenio',
    title: 'Cancelar convenio',
    permissions: [
      'cancelar_convenio',
    ],
  },
  {
    name: 'cancelar_convenio_y_abonos',
    title: 'Hacer devolución de parcialidades',
    permissions: [
      'cancelar_convenio_y_abonos',
    ],
  },
  {
    name: 'pagar_en_cortes_anteriores',
    title: 'Pagar en cortes anteriores',
    permissions: [
      'pagar_en_cortes_anteriores',
    ],
  },
  {
    name: 'imprimir_orden_pago',
    title: 'Imprimir orden de pago',
    permissions: ['view_referenciadepago', 'add_referenciadepago'],
    ventanilla: true,
  },
  {
    name: 'imprimir_opinion_cumplimiento',
    title: 'Imprimir opinión de cumplimiento',
    permissions: ['view_opiniondelasobligaciones'],
  },
  {
    name: 'imprimir_constancia_situacion_fiscal',
    title: 'Imprimir constancia de situación fiscal',
    permissions: ['view_constanciadesituacionmunicipal'],
  },
  {
    name: 'acreditar_recibo',
    title: 'Acreditar recibo',
    permissions: [
      'add_recibo',
      'view_recibo',
      'view_conceptodeacreditacion',
      'change_cargo',
    ],
  },
  {
    name: 'registrar_direccion',
    title: 'Registrar dirección',
    permissions: [
      'add_direccion',
      'view_catalogodepais',
      'view_estado',
      'view_municipio',
      'view_codigopostal',
      'view_tipodevialidad',
    ],
    ventanilla: true,
  },
];

const customSections = permissionSectionsPadrones.filter((e) => e.caja);

export const permissionConfigVentanilla = {
  mainPermission: 'view_ventanilla_en_menu',
  required,
  customActions: customActions.filter((a) => a.ventanilla),
  customSections,
};

export const permissionConfig = {
  mainPermission: 'view_caja_en_menu',
  required,
  customActions: customActions.filter((a) => a.name !== 'generar_convenio'),
  customSections,
};

const metodosDePagoMap = {
  EFECTIVO: 1,
  T_CREDITO: 4,
  T_DEBITO: 18,
  NETPAY: 24,
  DEPOSITO: 25,
};

const Caja = ({ permission, mostrarCobro: _mostrarCobro = true }) => {
  const mostrarCobro = _mostrarCobro && permission.cobrar;
  const { corte, alerta } = useCorte();
  const dispatch = useDispatch();
  const screen = useBreakpoint();
  const history = useHistory();
  const { theme /** , socketsConnected */ } = useSelector(({ app }) => app);
  const socketsConnected = true;
  const { entidad: _entidad, user } = useSelector(({ auth }) => auth);
  const tiposDePadrones = useSelector(({ catalogs }) => catalogs.padrones)
    .filter((p) => hasPadronPermission(p.id))
    .concat([
      { id: 0, descripcion: 'Por Folio de Trámite' },
      { id: -1, descripcion: 'Por Orden de Pago' },
    ]);

  const {
    netpayResponse,
    lastPaymentData,
    padrones: padronesStore,
    selectedRowKeys,
    pinPadronAgregarCargos,
    ciudadano,
    recibos,
  } = useSelector(({ caja }) => caja);
  const { data: configCancelacionesRecargos } = useConfigOmisionDeRecargos();
  const padrones = useMemo(() => padronesStore.map((padron) => ({
    ...padron,
    cargos: sortCargos(recalculateCargos(
      padron.cargos, selectedRowKeys[padron.key],
      configCancelacionesRecargos,
    )),
  })), [
    padronesStore,
    selectedRowKeys,
    configCancelacionesRecargos,
  ]);

  const somethingToPay = !!Object.keys(selectedRowKeys)
    .some((key) => selectedRowKeys[key].length);
  const {
    solo_pago_netpay,
    metodo_de_pago_principal = 1,
    padron_principal = 3,
    tipo_de_cargo: tipoDeCargoDeposito,
    consultar_infracciones_viales_relacionadas,
  } = _entidad;
  const metodoDefault = solo_pago_netpay ? metodosDePagoMap.NETPAY : metodo_de_pago_principal;
  const [formCiudadano] = Form.useForm();
  const [formPadron] = Form.useForm();
  const [formPago] = Form.useForm();
  const selectedMetodo = Form.useWatch('metodo', formPago);
  const [loading, setLoading] = useState(true);
  const [netPayLoading, setNetPayLoading] = useState(false);
  const [tipoDePadronSelected, setTipoDePadronSelected] = useState(padron_principal);
  const [_pinPadron, setPinPadron] = useState({});
  const pinPadron = padronesStore.find((p) => p.key === _pinPadron?.key);
  const [ciudadanoList, setCiudadanosList] = useState({ results: [], count: 1 });
  const [verMas, setVerMas] = useState(false);
  const [visibleBusqueda, setVisibleBusqueda] = useState(false);
  const [dataPagos, setDataPagos] = useState([]);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [padronesList, setPadronesList] = useState([]);
  const [visibleDatosDePago, setVisibleDatosDePago] = useState(false);
  const [editedPago, setEditedPago] = useState({});
  const [justPay, setJustPay] = useState(false);
  const [modalSocketsVisible, setModalSocketsVisible] = useState(false);
  const [visibleGetRecibos, setVisibleGetRecibos] = useState(false);
  const netpayRef = useRef();
  const inputCiudadanoRef = useRef();
  const inputImporteRef = useRef();
  const [base64, setBase64] = useState();
  const [folioOrdenDePago, setFolioOrdenDePago] = useState('');
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [_metodosPago, setMetodosPago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [searchQPadron, setSearchQPadron] = useState();
  const [unidadesRecaudadoras, setUnidadesRecaudadoras] = useState([]);
  const [saldoDisponibleEnDeposito, setSaldoDisponibleEnDeposito] = useState(0);
  const [cambio, setCambio] = useState(0);
  const [visibleModalConsulta, setVisibleModalConsulta] = useState(false);
  const [turnOnMSI, setTurnOnMSI] = useState(false);
  // const [msi, setMsi] = useState();
  const [visibleCalc, setVisibleCalc] = useState(false);
  const [isNormalReference, setIsNormalReference] = useState(false);
  const msiRef = useRef('');
  const debounceRef = useRef();
  const plantillasID = [9, 12, 13, 14, 16, 17, 18, 19, 26, 27, 29, 74, 75];

  const [visibleConvenio, setVisibleConvenio] = useState(false);

  const [visibleForm, setVisibleForm] = useState(false);

  const filterMetodosDePago = (e) => (solo_pago_netpay
    ? [metodosDePagoMap.T_CREDITO, metodosDePagoMap.T_DEBITO, metodosDePagoMap.NETPAY]
      .includes(e.id) : true);
  const hasDepositoInCargos = () => {
    const selectedCargos = selectedRowKeys[ciudadano?.key];
    if (selectedCargos?.length) {
      const { cargos } = padrones.find((p) => p.key === ciudadano.key);
      const _cargos = selectedCargos.map((id) => cargos.find((c) => c.id === id));
      return _cargos.some((c) => c.tipo_de_cargo?.id === tipoDeCargoDeposito);
    }
    return false;
  };

  const mapperMetodosDePago = (m) => ({
    ...m,
    // eslint-disable-next-line max-len
    disabled: (m.id === metodosDePagoMap.NETPAY && (!!dataPagos.length || !corte?.terminal_de_netpay?.serial))
      // eslint-disable-next-line max-len
      || (m.id === metodosDePagoMap.EFECTIVO && dataPagos.some((dp) => dp.metodo === metodosDePagoMap.EFECTIVO))
      // eslint-disable-next-line max-len
      || (m.id === metodosDePagoMap.DEPOSITO && (hasDepositoInCargos() || dataPagos.some((dp) => dp.metodo === metodosDePagoMap.DEPOSITO))),
  });
  const activePublicoGeneral = ciudadano
    && ciudadano.id === _entidad.ciudadano_publico_en_general;
  const metodosPago = _metodosPago.map(mapperMetodosDePago);

  // Función auxiliar para calcular el total de un padrón específico
  const calculatePadronTotal = useCallback((padron) => {
    const selectedCargos = selectedRowKeys[padron.key];
    return getTotalPadron(padron.cargos, selectedCargos);
  }, [selectedRowKeys]);

  // Función auxiliar para actualizar el formulario de pago
  const updatePaymentForm = useCallback((amount) => {
    formPago.setFieldsValue({ importe: amount });
  }, [formPago]);

  // Cálculo del total optimizado
  const total = useMemo(() => {
    const calculatedTotal = padrones.reduce((accumulator, padron) => {
      const padronTotal = calculatePadronTotal(padron);
      return accumulator + padronTotal;
    }, 0);

    const roundedTotal = round(calculatedTotal) || 0;
    updatePaymentForm(roundedTotal);

    return roundedTotal;
  }, [padrones, calculatePadronTotal, updatePaymentForm]);

  const isButtonDisabled = () => {
    const formValues = formPago.getFieldsValue();
    const importe = formValues?.importe || 0;

    // Verificar condiciones básicas
    if (!ciudadano || !somethingToPay) return true;

    // Verificar si el método de pago es NETPAY
    if (selectedMetodo === metodosDePagoMap.NETPAY) return true;

    if ((selectedMetodo === metodosDePagoMap.EFECTIVO) && cambio > 0) return true;

    // Verificar condiciones de cambio y método de pago
    if (cambio >= 0) return true;

    // Verificar que el total y el importe sean mayores a 0
    if (total === 0 || importe === 0) return true;

    return false;
  };

  const getMSIOptions = () => {
    let _options = [];
    if (total >= 300 && total < 600) {
      _options = [3];
    } else if (total >= 600 && total < 900) {
      _options = [3, 6];
    } else if (total >= 900 && total < 1200) {
      _options = [3, 6, 9];
    } else if (total >= 1200 && total < 1800) {
      _options = [3, 6, 9, 12];
    } else if (total >= 1800) {
      _options = [3, 6, 9, 12, 18];
    } else if (total < 300) {
      return [];
    }
    return _options.map((e) => ({
      label: e,
      value: `${e}`.length === 1 ? `0${e}` : `${e}`,
    }));
  };

  const optionsMSI = getMSIOptions();

  const getTotalMetodos = (
    _dataPagos = dataPagos,
  ) => {
    const _total = _dataPagos.reduce((acum, curr) => acum + parseFloat(curr.importe), 0);
    return parseFloat(_total.toFixed(2));
  };

  const getFaltante = (_dataPagos = dataPagos) => Math.max(total - getTotalMetodos(_dataPagos), 0);

  const ModalSockets = () => (
    <Modal
      title="Pago con Netpay"
      icon={<QuestionCircleOutlined />}
      open={modalSocketsVisible}
      onOk={() => setModalSocketsVisible(false)}
      okText="Aceptar"
      cancelButtonProps={{ style: { display: 'none' } }}
      closable={false}
    >
      <Typography.Text>
        El método de pago no responde correctamente. Puede continuar cobrando pero
        es posible que tenga que buscar los recibos manualmente.
      </Typography.Text>
      <br />
      <br />
      <Typography.Paragraph>
        Se le notificará si el servicio vuelve a estar disponible.
      </Typography.Paragraph>
    </Modal>
  );

  const focusInputImporte = () => setTimeout(() => inputImporteRef.current.focus());

  const onChangeImporte = (e) => {
    const { selectionStart, value } = e.target;
    const importe = parseFloat(value || 0);

    // Calcular el total de pagos existentes (excluyendo el que se está editando)
    const pagosExistentes = getTotalMetodos(dataPagos.filter((d) => d.key !== editedPago?.key));

    // Calcular el nuevo total de datos de pago
    const totalDatosDePago = parseFloat((pagosExistentes + importe).toFixed(2));

    // Calcular el cambio
    const nuevoCambio = parseFloat((totalDatosDePago - total).toFixed(2));
    setCambio(nuevoCambio);

    focusInputImporte();

    // Mantener la posición del cursor
    setTimeout(() => {
      const len = selectionStart.length;
      const position = Math.max(selectionStart > len ? len : selectionStart, 1);
      inputImporteRef.current.input.selectionStart = position;
      inputImporteRef.current.input.selectionEnd = position;
    });
  };

  useEffect(() => {
    if (process.env.REACT_APP_SOCKETS_URL && mostrarCobro) {
      if (!socketsConnected) {
        const netpayInDataPagos = dataPagos.some((e) => e.metodo === metodosDePagoMap.NETPAY);
        if ((solo_pago_netpay || selectedMetodo === metodosDePagoMap.NETPAY || netpayInDataPagos)
          && !modalSocketsVisible) {
          setModalSocketsVisible(true);
        }
      } else if (modalSocketsVisible) {
        setModalSocketsVisible(false);
        message.info('');
      }
    }
    // eslint-disable-next-line
  }, [socketsConnected, dataPagos, selectedMetodo]);

  const hasPermissionModifyConfigFolios = hasActionPermission('entidad', ActionsNames.viewcrud)
    && hasActionPermission('configuraciondeusodelfolio', ActionsNames.view)
    && hasActionPermission('configuraciondeusodelfolio', ActionsNames.change);

  const showMsg = (msg, action) => {
    message.info((
      <>
        <span>
          {msg}
        </span>
        {hasPermissionModifyConfigFolios && (
          <Button
            type="link"
            onClick={() => action()}
          >
            Ir
          </Button>
        )}
      </>
    ), 5);
  };

  const asignarEntidad = async (_user) => {
    setLoading(true);
    const { razon_social, id: entidadId } = _entidad;
    const { entidad, nombre_completo, id } = _user;
    entidad.push(entidadId);
    const edited = await patchCiudadano(
      id,
      { entidad },
      `Ahora el ciudadano ${nombre_completo} también pertenece a ${razon_social}.`,
    );
    if (edited) {
      // eslint-disable-next-line no-use-before-define
      await handleSearch(edited.clave_ciudadana);
      formPadron.resetFields();
    }
    setLoading(false);
  };

  const msgAsignarEntidad = (_user) => (
    message.info({
      content: (
        <>
          <Row style={{ width: '100%' }}>
            El contribuyente no pertenece a la entidad.
          </Row>
          <Row justify="center">
            <strong style={{ marginRight: 3 }}>
              ¿Desea asignarlo a esta entidad?
            </strong>
          </Row>
          <Row align="middle" justify="center" style={{ width: '100%' }}>
            <Button
              // type="link"
              onClick={() => asignarEntidad(_user)}
              className="success"
            >
              Si
            </Button>
            <Button
              onClick={() => {
                message.destroy();
                formCiudadano.resetFields();
              }}
              className="danger"
            // type="link"
            >
              No
            </Button>
          </Row>
        </>

      ),
      duration: 0,
    })
  );

  const focusInputCiudadano = () => setTimeout(() => inputCiudadanoRef.current?.focus());

  const clear = (clearAll) => {
    setCiudadanosList({ results: [], count: 0 });
    formPago.setFieldsValue({
      metodo: metodoDefault,
      importe: 0.00,
    });
    setDataPagos([]);
    setVerMas(false);
    setCambio(0);
    dispatch({ type: CLEAN_CAJA, payload: !clearAll });
    if (clearAll) {
      setPinPadron({});
      formCiudadano.resetFields();
      focusInputCiudadano();
      setTipoDePadronSelected(padron_principal);
      formPadron.resetFields();
    }
  };

  usePermissionMessage(permission);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      checkToken();
      setLoading(true);
      const promises = [
        getUnidadesDeRecaudacion(),
      ];
      if (mostrarCobro) {
        promises.push(getConfiguracionesDeUsosDeFolios());
        promises.push(getMetodosDePago());
        promises.push(getBancos());
      }
      const [
        _unidadesRecaudadoras,
        ...mostrarCobroCatalogos
      ] = await Promise.all([
        ...promises,
        getTiposDeVehiculos(),
        getClasesDeVehiculos(),
        getEstadosDeVehiculo(),
        getLineasDeVehiculos(),
        getMarcasDeVehiculos(),
        getCatalogoDePadrones(),
        getServiciosDeVehiculo(),
        getTiposDeEstablecimientos(),
      ]);
      if (mounted) {
        if (mostrarCobro) {
          const [
            dataFolios,
            dataMetodosDePago,
            dataBancos,
          ] = mostrarCobroCatalogos;
          const _metodosDePago = dataMetodosDePago.map((e) => {
            if (e.id === metodosDePagoMap.EFECTIVO) return { ...e, prioridad: 3 };
            if (e.id === metodosDePagoMap.T_CREDITO) return { ...e, prioridad: 1 };
            if (e.id === metodosDePagoMap.T_DEBITO) return { ...e, prioridad: 2 };
            return { ...e, prioridad: 4 };
          }).sort((a, b) => a.prioridad - b.prioridad)
            .filter(filterMetodosDePago).map(mapperMetodosDePago);
          const usosIds = dataFolios.map((e) => e?.uso_de_folio);
          if (!usosIds.includes(1) || !usosIds.includes(2)) {
            showMsg('No hay configuración de Folios para el recibo', () => {
              history.push('/configuracion/entidad?tab=folios');
              message.destroy();
            });
          }
          setMetodosPago(_metodosDePago);
          setBancos(dataBancos);
        }
        setUnidadesRecaudadoras(_unidadesRecaudadoras);
        setLoading(false);
      }
      focusInputCiudadano();
    };
    if (permission.hasAllViewPermission) {
      fetchData();
    }
    formPago.setFieldsValue({
      metodo: metodoDefault,
      importe: 0.00,
    });
    if (ciudadano) {
      formCiudadano.setFieldsValue({ q: padronToString(ciudadano, 1) });
      setPinPadron(ciudadano);
    }
    return () => { mounted = false; clear(true); };
    // eslint-disable-next-line
  }, []);

  const netpayFeedback = ({ status_code, message: netPayMessage }) => {
    const error = {
      '00': 'success',
      '02': 'warn',
    };
    const type = error[status_code] ?? 'info';
    message[type](netPayMessage, 5);
  };

  useEffect(() => {
    if (recibos?.length) history.push('/recibos-de-caja?from=caja');
  }, [recibos]);

  useEffect(() => {
    if (mostrarCobro && netpayResponse) {
      netpayFeedback(netpayResponse);
      dispatch({ type: CLEAR_NETPAY_RESPONSE });
      setNetPayLoading(false);
      clearTimeout(netpayRef.current);
      netpayRef.current = null;
      setVisibleGetRecibos(false);
    }
    // eslint-disable-next-line
  }, [netpayResponse]);

  const addPadron = async (id, tipoDePadron = tipoDePadronSelected, callback) => {
    const match = padrones.find((padron) => padron.key === `${id}_${tipoDePadron}`);
    if (match) {
      message.info(`${padronToString(match, tipoDePadron, true)} ya se encuentra agregado`);
    } else {
      setLoading(true);
      const padron = await retrievePadronesMap[tipoDePadron](
        id,
        padronesEnum.LOTES_DE_PANTEON === tipoDePadron,
      );
      if (padron) {
        padron.tipo_de_padron = tipoDePadron;
        padron.key = `${padron.id}_${padron.tipo_de_padron}`;
        padron.es_publico_general = padron.id === _entidad.ciudadano_publico_en_general
          && tipoDePadron === 1;
        delete padron.tramite;
        if (callback) {
          callback(padron);
        }
        dispatch({
          type: ADD_PADRON,
          payload: padron,
        });
        formPadron.resetFields();
        setPinPadron(padron);
      }
      setLoading(false);
    }
  };

  const addMultiplesPadrones = async (pdrn, tipoDePadron = tipoDePadronSelected, callback) => {
    const matches = pdrn.map((e) => padrones.filter((padron) => padron.key === `${e.id}_${tipoDePadron}`)).flat();
    if (matches.length) {
      message.info(`${padronToString(matches, tipoDePadron, true)} ya se encuentra agregado`);
    } else {
      setLoading(true);
      const _padronesIds = pdrn.map((e) => e.id);
      const promises = _padronesIds.map((e) => retrievePadronesMap[tipoDePadron](e));
      const _padrones = (await Promise.all(promises));
      if (_padrones.length) {
        _padrones.forEach((e) => {
          e.tipo_de_padron = tipoDePadron;
          e.key = `${e.id}_${e.tipo_de_padron}`;
          e.es_publico_general = e.id === _entidad.ciudadano_publico_en_general
            && tipoDePadron === 1;
          delete e.tramite;
        });
        if (callback) {
          callback(_padrones);
        }
        dispatch({
          type: ADD_PADRONES,
          payload: _padrones,
        });
        formPadron.resetFields();
      }
      setLoading(false);
    }
  };

  const getCargosAPagar = async (_ciudadano) => {
    setLoading(true);
    const _padrones = await getConsultarCargosCiudadanos(_ciudadano.id);
    if (_padrones.length < 10) {
      _padrones.forEach((p, idx) => {
        _padrones[idx].requiereFetch = true;
      });
    }
    if (_padrones.length) {
      setPinPadron(_padrones[0]);
      dispatch({
        type: SET_PADRONES,
        payload: _padrones,
      });
      formCiudadano.setFieldsValue({
        q: padronToString(_padrones[0], 1),
      });
      if (_padrones.length >= 10) {
        setTimeout(() => setVisibleModalConsulta(true));
      }
    }
    setLoading(false);
  };

  const onClickPublicoGeneral = async () => {
    if (activePublicoGeneral) {
      clear(true);
    } else if (!_entidad?.ciudadano_publico_en_general) {
      message.warn({
        content: (
          <>
            <span>
              Esta entidad no tiene configurado un ciudadano
              para el cobro de público general
            </span>
            <Button
              type="link"
              onClick={() => {
                history.push('/configuracion/entidad/?tab=configurations');
                message.destroy();
              }}
            >
              Ir
            </Button>
          </>
        ),
      });
    } else {
      await addPadron(_entidad.ciudadano_publico_en_general, 1, (padron) => {
        setPinPadron(padron);
        formCiudadano.setFieldsValue({ q: padronToString(padron, 1) });
      });
    }
  };

  const ButtonPublicoGeneral = () => (
    _entidad.ciudadano_publico_en_general && permission.cobrar_publico_general
      ? (
        <Col>
          <Button
            size="large"
            onClick={onClickPublicoGeneral}
            disabled={ciudadano && !activePublicoGeneral}
            style={{ borderRadius: 4 }}
            icon={activePublicoGeneral
              ? <CheckCircleFilled className="success" />
              : <MinusCircleFilled className="disabled" />}
          >
            Público general
          </Button>
        </Col>
      )
      : null
  );

  const handleSearch = async (q) => {
    try {
      setLoading(true);
      if (q?.length >= 4 && q) {
        formatParams(q);
        const result = await getCiudadanoCaja({ q });
        if ([0, 1].includes(result?.results?.length)) {
          const [_ciudadano] = result.results;
          if (_ciudadano) {
            if (_ciudadano.fromThisEntity) {
              await getCargosAPagar(_ciudadano);
              setVerMas(true);
            } else {
              msgAsignarEntidad(_ciudadano);
            }
          } else {
            setVisibleBusqueda(true);
            setCiudadanosList({});
            message.warn('Ciudadano no encontrado.');
          }
        } else {
          setCiudadanosList(result);
          setVisibleBusqueda(true);
        }
      } else if (!q) {
        message.info('Introduza algún dato del ciudadano para comenzar la búsqueda');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleSearchFolio = async ({ q }) => {
    try {
      setLoading(true);
      if (!q) {
        message.warn('Introduzca un folio para comenzar la búsqueda');
        setLoading(false);
        return;
      }

      const result = await postConsultaFolio({ folio: q.trim() });
      if (!result) {
        message.warn('No se encontró el Folio o aún no se han generado los cargos');
        setLoading(false);
        return;
      }

      const tipoDePadron = getTipoDePadronPorContenType(result.cargos[0]?.content_type);
      const _padron = await retrievePadronesMap[tipoDePadron](result.cargos[0]?.object_id);
      let padron = {
        ..._padron,
        ...result,
      };

      if (!padron) {
        message.warn('Padron eliminado', 5);
        return;
      }

      padron.keepCargos = true;
      padron.requiereFetch = false;
      padron.cargos = result.cargos;
      padron.plantillas = result.plantillas;
      padron.tramites_bloqueando_adeudos = result.tramites_bloqueando_adeudos;
      padron.searchedByFolio = true;
      padron.tipo_de_padron = tipoDePadron;

      if (result.es_referencia) {
        padron.folio_de_referencia = q;
        padron.observaciones = result?.observaciones_complemento;
        padron.key = `${padron?.id}_${tipoDePadron}__referencia_${padron.folio_de_referencia}`;
      } else {
        padron.tramite = result.cargos[0]?.tramite;
        padron.disabledOrdenPago = result.plantillas
          .find((p) => p.id === padron.tramite?.plantilla)?.disabledOrdenPago;
        if (result.plantillas.find((p) => p.id === padron.tramite?.plantilla)?.use_json) {
          padron = {
            ...padron,
            ...padron.json_predio,
            alerta: padron.alerta,
          };
        }
        padron.tramite = result.cargos[0]?.tramite;

        padron.key = `${padron?.id}_${tipoDePadron}__tramite_${padron.tramite.id}`;
      }

      if (padron.cargos.some((c) => c.convenios)) {
        padron.key += '__convenio';
        const cargoConConvenio = padron.cargos.find((c) => c.convenios);
        padron.convenio = cargoConConvenio.convenios.id;
        padron._convenio = cargoConConvenio.convenios;
      }
      // DISCLAIMER NO BUSCAR UN PADRON...
      /// Y LUEGO HACER CONSULTAS DE FOLIO DE ORDENES DE PAGO/TRAMITE DEL MISMO PADRON

      const matches = padrones
        .find((item) => item.folio_de_referencia === padron.folio_de_referencia);
      if (matches && result.es_referencia) {
        message.info(`Ya se encuentra agregado la referencia con el folio ${matches.folio_de_referencia}`);
        return;
      }

      const idsCargos = padron.cargos.map((e) => e.id);
      const match = padrones.find((item) => item.key.includes(`${padron?.id}_${tipoDePadron}`)
            && item.cargos.map((e) => e.id).some((c) => idsCargos.includes(c)));

      if (match) {
        message.info(`${padronToString(match, tipoDePadron, true)} Algunos cargos en la consulta del folio ya se encuentran agregados en el panel del padrón`);
        return;
      }

      dispatch({
        type: ADD_PADRON,
        payload: padron,
      });
      formPadron.resetFields();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const searchCiudadano = () => {
    const values = formCiudadano.getFieldsValue();
    if (values) {
      handleSearch(values.q);
    }
  };

  const imprimirObligaciones = async () => {
    setLoading(true);
    const { id, tipo_de_padron } = pinPadron;
    const result = await postImprimirObligaciones(id, tipo_de_padron);
    if (result.success) {
      setBase64(result.base64);
      setVisiblePreview(true);
    }
    setLoading(false);
  };

  const imprimirConstancia = async (ciudadanoID) => {
    setLoading(true);
    const result = await getConstancia(ciudadanoID);
    if (result.success) {
      setBase64(result.base64);
      setVisiblePreview(true);
    }
    setLoading(false);
  };

  const imprimirEstadoDeCuenta = async () => {
    const values = {
      ID: 24, // 72,
      Parametros: [
        {
          Nombre: 'parameter1',
          TipoParametro: 'Entero',
          Valor: pinPadron?.id,
        },
      ],
    };
    const file = await postObtenerReporte(values);
    if (file) {
      setBase64(file);
      setVisiblePreview(true);
    }
  };

  const imprimirOrdenDePago = async (extraValues = {}, referencia_normal = true) => {
    setLoading(true);
    const observaciones_complemento = pinPadron?.observaciones;
    const _selectedRowKeys = selectedRowKeys[pinPadron.key];
    const { cargos: _cargos } = padrones.find((p) => p.key === pinPadron.key);
    const cargos = _cargos.filter((c) => _selectedRowKeys.includes(c.id));
    const ciudadanoInfraccion = (pinPadron?.tipo_de_padron === 19 && pinPadron?.ciudadano)
      ? pinPadron?.ciudadano || pinPadron?.ciudadano?.id
      : _entidad.ciudadano_publico_en_general;

    const idsDescuentos = cargos.map((c) => [
      ...c.descuentos_aplicables,
      ...c.descuentos_especiales,
      ...c.recargos.map((r) => r.descuentos),
      ...c.recargosMulta.map((r) => r.descuentos),
      ...c.actualizaciones.map((a) => a.descuentos),
      ...c.gastos.map((g) => g.descuentos),
      ...c.gastosMulta.map((g) => g.descuentos),
    ]).flat(2).filter((d) => d.aplicado).map((d) => d.id);
    //
    if (!cargos.length) {
      const padronAsString = padronToString(pinPadron, pinPadron.tipo_de_padron);
      const warnNoCargos = () => message.warn(`No hay cargos seleccionados en el Padrón ${padronAsString}`);
      if (pinPadron.tipo_de_padron === 3) {
        try {
          const file = await postObtenerReporte({
            ID: 125,
            Parametros: [
              {
                Nombre: 'predio',
                TipoParametro: 'Entero',
                Valor: pinPadron?.id,
              },
              {
                Nombre: 'fecha_de_impresion',
                TipoParametro: 'Fecha',
                Valor: toMoment(new Date()).format(DATE_FORMAT),
              },
              {
                Nombre: 'usuario_imprimio',
                TipoParametro: 'Entero',
                Valor: user?.id,
              },
            ],
          });
          if (file) {
            setBase64(file);
            setVisiblePreview(true);
          } else {
            warnNoCargos();
          }
        } catch {
          warnNoCargos();
        }
        setLoading(false);
        return;
      }
      warnNoCargos();
      setLoading(false);
      return;
    }
    const totalAmount = getTotalPadron(_cargos, _selectedRowKeys);
    const lastDayInstance = toMoment(new Date()).endOf('month');
    const fechaBilling = lastDayInstance.format(DATE_FORMAT);
    const fecha = lastDayInstance.format(DATE_NETPAY_FORMAT);

    const result = await postImprimirOrdenDePago(
      totalAmount,
      fecha,
      fechaBilling,
      _selectedRowKeys,
      pinPadron.id,
      pinPadron.tipo_de_padron,
      pinPadron.tipo_de_padron === 19 ? ciudadanoInfraccion : ciudadano?.id,
      referencia_normal,
      extraValues,
      null,
      idsDescuentos,
      observaciones_complemento,
      extraValues.otro_formato || false,
      false,
      extraValues.orden_anual || false,
    );
    if (result.success) {
      setBase64(result.base64);
      setFolioOrdenDePago(result.folio);
      setVisiblePreview(true);
    }
    if (!isNormalReference) {
      setIsNormalReference(true);
    }
    setLoading(false);
  };

  const generarConvenio = () => {
    const padron = { ...pinPadron };
    if (_entidad.ciudadano_publico_en_general === padron.id) {
      message.warn('No es posible crear un convenio para los cargos del público general.');
      return;
    }
    if (padron.key.includes('convenio')) {
      message.warn('Estos cargos ya son parte de un convenio.');
      return;
    }
    const selectedKeys = selectedRowKeys[padron.key];
    if (!selectedKeys?.length) {
      message.warn('No hay cargos seleccionados en el Padrón para realizar esta acción');
      return;
    }
    setVisibleConvenio(true);
  };

  const InfoToSeeMore = () => {
    if (ciudadano) {
      const keys = Object.keys(ciudadano);
      const allCols = keys.map((item, i) => {
        const allowedKeys = ['clave_ciudadana', 'nombre_completo', 'RFC', 'CURP', 'INE'];
        if (allowedKeys.includes(item)) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={i}>
              <Text style={{ textTransform: 'capitalize' }}>
                {' '}
                {`${item.replace('_', ' ')}: `}
              </Text>
              {ciudadano[item]}
            </React.Fragment>
          );
        }
        return null;
      });
      return (
        <Row gutter={[5, 5]}>
          {allCols.filter((e) => e !== null).map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Col span={24} key={i}>
              {e}
            </Col>
          ))}
          {([1, 2].includes(pinPadron?.tipo_de_padron))
            && permission.imprimir_opinion_cumplimiento && !IPROVINAY && (
              <Col span={24}>
                <Button
                  onClick={imprimirObligaciones}
                  className="caja-action-button"
                  disabled={!pinPadron?.key}
                >
                  Imprimir Opinión de Cumplimiento
                </Button>
              </Col>
          )}
          {(pinPadron?.tipo_de_padron === 1)
            && permission.imprimir_constancia_situacion_fiscal && !IPROVINAY && (
              <Col span={24}>
                <Button
                  onClick={() => imprimirConstancia(pinPadron.id)}
                  className="caja-action-button"
                  disabled={!pinPadron?.key}
                >
                  Imprimir Constancia de Situación Fiscal
                </Button>
              </Col>
          )}
        </Row>
      );
    }
    return <span />;
  };
  // Padron
  const handleSearchPadron = async (q) => {
    try {
      if (!(q && q?.length > 0)) return;
      setLoading(true);

      const esCiudadano = tipoDePadronSelected === padronesEnum.CIUDADANO;
      const _padrones = await getPadronesMap[tipoDePadronSelected]({ q });
      const haveToAsignEntity = _padrones && !(Array.isArray(_padrones) || 'count' in _padrones);

      if (haveToAsignEntity) {
        const [_ciudadano] = _padrones.ciudadano;
        msgAsignarEntidad(_ciudadano);
        setLoading(false);
        return;
      }

      const isJustOneResultOrZero = [1, 0].includes(_padrones.length)
        || [1, 0].includes(_padrones?.count);

      if (!isJustOneResultOrZero
        && (consultar_infracciones_viales_relacionadas
          && tipoDePadronSelected === padronesEnum.INFRACCIONES)) {
        const _data = (_padrones?.results || _padrones);
        await addMultiplesPadrones(_data, tipoDePadronSelected);
        setLoading(false);
        return;
      }

      if (!isJustOneResultOrZero) {
        setPadronesList(_padrones);
        setVisibleBusquedaPadrones(true);
        setSearchQPadron(q);
        setLoading(false);
        return;
      }

      const found = _padrones?.[0] || _padrones?.results?.[0];
      if (!found) {
        message.warn('Padrón no encontrado');
        setLoading(false);
        return;
      }

      const match = padrones.find((item) => item.key.includes(`${found?.id}_${tipoDePadronSelected}`));

      if (match) {
        message.info(`${padronToString(match, match.tipo_de_padron, true)} ya se encuentra agregado`);
        setLoading(false);
        return;
      }

      if (esCiudadano && 'fromThisEntity' in found && !found.fromThisEntity) {
        msgAsignarEntidad(found);
      } else {
        await addPadron(found.id);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const searchPadron = () => {
    const values = formPadron.getFieldsValue();
    if (values && !loading) {
      handleSearchPadron(values.q);
    }
  };

  useEffect(() => {
    if (ciudadano && selectedRowKeys[ciudadano.key]) {
      const disableDeposito = hasDepositoInCargos();
      if (disableDeposito && selectedMetodo === metodosDePagoMap.DEPOSITO) {
        formPago.setFieldsValue({
          metodo: [metodosDePagoMap.DEPOSITO, metodosDePagoMap.NETPAY].includes(metodoDefault)
            // eslint-disable-next-line max-len
            ? metodosPago.filter((e) => ![metodosDePagoMap.DEPOSITO, metodosDePagoMap.NETPAY].includes(e.id))[0]?.id
            : metodoDefault,
          importe: total,
        });
        message.warn('No se puede pagar un depósito en garantía con otro depósito en garantía.');
      }
    }
  }, [ciudadano, selectedMetodo, selectedRowKeys[ciudadano?.key]]);

  const rules = {
    required: [{ required: true }],
    importe: [
      {
        validator: async (_, val = 0) => {
          const { metodo } = formPago.getFieldsValue();
          if (val <= 0) {
            throw new Error('El importe debe ser mayor a 0');
          }
          if (val && metodo === metodosDePagoMap.DEPOSITO) {
            if (val > saldoDisponibleEnDeposito) {
              throw new Error('El importe supera el saldo disponible por deposito en garantia.');
            }
          }
        },
      },
    ],
  };

  const onChangeDataPagos = (_dataPagos) => {
    const metodosPagoUnicos = [metodosDePagoMap.EFECTIVO, metodosDePagoMap.NETPAY];
    const faltante = getFaltante(_dataPagos);
    const formPagoValues = { importe: faltante };
    // eslint-disable-next-line max-len
    if (_dataPagos.some((dp) => dp.metodo === metodosDePagoMap.EFECTIVO)) {
      // si se agrego efectivo ya no se puede elegit ni netpay o efectivo
      // se elije el primero de los disponibles
      formPagoValues.metodo = metodosPagoUnicos.includes(metodoDefault)
        ? metodosPago.filter((e) => !metodosPagoUnicos.includes(e.id))[0]?.id
        : metodoDefault;
    } else if (dataPagos.some((dp) => dp.metodo === metodosDePagoMap.DEPOSITO)) {
      // si se agrego deposito ya no se puede elegit ni netpay o deposito
      // eslint-disable-next-line max-len
      formPagoValues.metodo = [metodosDePagoMap.DEPOSITO, metodosDePagoMap.NETPAY].includes(metodoDefault)
        // eslint-disable-next-line max-len
        ? metodosPago.filter((e) => ![metodosDePagoMap.DEPOSITO, metodosDePagoMap.NETPAY].includes(e.id))[0]?.id
        : metodoDefault;
    } else if (_dataPagos.length) {
      // si el metodo default es netpay se elije otro ya que no se puede combinar
      formPagoValues.metodo = metodoDefault === metodosDePagoMap.NETPAY
        ? metodosPago.filter((e) => !metodosPagoUnicos.includes(e.id))[0]?.id
        : metodoDefault;
    } else {
      formPagoValues.metodo = metodoDefault;
    }
    formPago.setFieldsValue(formPagoValues);
  };

  const agregarDatoDePago = async (datoDePago = null, _handlePay = false) => {
    try {
      const values = getFormatedValues({ ...formPago.getFieldsValue(), ...datoDePago });
      let copyDataPagos = [...dataPagos];
      copyDataPagos.push({
        ...editedPago,
        ...values,
        importe: parseFloat(values.importe),
      });
      if (!_handlePay) {
        copyDataPagos = copyDataPagos.map((e, idx) => ({ ...e, key: idx }));
        setDataPagos(copyDataPagos);
      }
      if (!solo_pago_netpay && !_handlePay) {
        setVisibleDatosDePago(false);
        setEditedPago();
        onChangeDataPagos(copyDataPagos);
        setCambio(0);
      }
      if (_handlePay) {
        // eslint-disable-next-line no-use-before-define
        setTimeout(() => handlePay(copyDataPagos));
      }
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const requireDatosBancarios = async (_handlePay = false) => {
    try {
      await formPago.validateFields();
      const values = formPago.getFieldsValue();
      if (values.importe === 0) {
        message.warn('El importe debe ser 0');
        return;
      }
      const requiere_datos_bancarios = metodosPago
        .find((e) => e.id === selectedMetodo)?.requiere_datos_bancarios;
      if (requiere_datos_bancarios) {
        setVisibleDatosDePago(true);
        setJustPay(_handlePay);
      } else {
        agregarDatoDePago(null, _handlePay);
      }
    } catch (err) {
      onError(err);
    }
  };

  const isValid = (_dataPagos) => {
    const _cambio = typeof cambio === 'string' ? parseFloat(cambio) : cambio;
    const importeEfectivo = _dataPagos
      .find((item) => item.metodo === metodosDePagoMap.EFECTIVO)?.importe || 0;

    if (!_cambio) {
      return true;
    }

    if (importeEfectivo) {
      if (_cambio < importeEfectivo) {
        return true;
      }
      message.warn('El cambio no puede ser mayor al importe del efectivo');
      return false;
    }
    message.warn('No puede haber cambio con los métodos de pago seleccionados');
    return false;
  };

  // Cuando el total a pagar es 0, se asigna el método de pago en efectivo
  // para poder pagar
  useEffect(() => {
    if (total === 0 && Object.keys(selectedRowKeys)
      .map((key) => selectedRowKeys[key].length).some((e) => e)) {
      formPago.setFieldsValue({
        metodo: metodosDePagoMap.EFECTIVO,
        importe: 0,
      });
    }
  }, [total, selectedRowKeys]);

  const getLocalPaymentFormatedData = (_dataPagos) => {
    const _total = total;
    const metodos = _dataPagos.map((dp) => {
      if (dp.metodo === metodosDePagoMap.EFECTIVO) {
        return {
          ...dp,
          importe: _dataPagos.length === 1 ? _total : dp.importe - cambio,
        };
      }
      if (dp.metodo === metodosDePagoMap.NETPAY) {
        return {
          ...dp,
          importe: _total,
        };
      }
      return dp;
    });

    const _padrones = padrones.filter((p) => selectedRowKeys[p.key].length).map((padron) => {
      const _selectedRowKeys = selectedRowKeys[padron.key];
      const cargosSeleccionados = _selectedRowKeys;
      const cargos = padron.cargos.filter((c) => cargosSeleccionados.includes(c.id));
      const tramitesCargos = cargos.map((c) => c.tramite);
      const cargosCancelarDescuentos = getDistinctValues(cargos
        .filter((c) => !c.aplicaDescuentoPorPeriodo && c.cargosPeriodo.length)
        .map((c) => c.cargosPeriodo).flat()).filter((c) => !_selectedRowKeys.includes(c));
      // mapear cargos no seleccionados para obtener descuentos a cancelar
      const descuentosPorCancelar = cargosCancelarDescuentos.map((id) => {
        const cargo = padron.cargos.find((c) => c.id === id);
        const descuentos = [
          ...cargo.descuentos_aplicables,
          ...cargo.descuentos_especiales,
          ...cargo.recargos.map((r) => r.descuentos),
          ...cargo.recargosMulta.map((r) => r.descuentos),
          ...cargo.actualizaciones.map((a) => a.descuentos),
          ...cargo.gastos.map((g) => g.descuentos),
          ...cargo.gastosMulta.map((g) => g.descuentos),
        ];
        return descuentos;
      }).flat(2).filter((d) => d.aplica_por_periodo);

      const descuentos = cargos.map((c) => [
        ...c.descuentos_aplicables,
        ...c.descuentos_especiales,
        ...c.recargos.map((r) => r.descuentos),
        ...c.recargosMulta.map((r) => r.descuentos),
        ...c.actualizaciones.map((a) => a.descuentos),
        ...c.gastos.map((g) => g.descuentos),
        ...c.gastosMulta.map((g) => g.descuentos),
      ]).flat(2).filter((d) => d.aplicado).map((d) => d.id);
      // Se cancelan los descuentos por periodo que no se esten aplicando
      return {
        descuentosPorCancelar,
        cargos: cargosSeleccionados,
        padron_id: padron.id,
        tipo_de_padron: padron.tipo_de_padron,
        observaciones: padron.observaciones || '',
        importe: getTotalPadron(padron.cargos, cargosSeleccionados),
        importe_sin_redondeo: getTotalSinRound(padron.cargos, cargosSeleccionados),
        referencia_de_pago: padron?.folio_de_referencia || '',
        descuentos,
        json_bool: tramitesCargos.some((t) => plantillasID.includes(t?.plantilla)),
      };
    });
    const ciudadanoInfraccion = (tipoDePadronSelected === 19 && !ciudadano?.ciudadano?.id)
      ? _entidad.ciudadano_publico_en_general
      : ciudadano.ciudadano?.id;

    return {
      metodos_de_pago: metodos,
      padrones: _padrones,
      ciudadano: tipoDePadronSelected === 19 ? ciudadanoInfraccion : ciudadano?.id,
      json_bool: _padrones.some((p) => p.json_bool),
    };
  };

  const debouncePostRecibos = async (_dataPagos) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      const values = getLocalPaymentFormatedData(_dataPagos);
      const promisesCancelarDescuentos = values.padrones
        .map((p) => p.descuentosPorCancelar).flat().map((d) => putDescuentos({
          motivo_de_cancelacion: 'CANCELACION AUTOMATICA POR PAGO',
          estado_del_descuento: 3,
          estados_globales: 5,
        }, d.id));
      if (promisesCancelarDescuentos.length) {
        await Promise.all(promisesCancelarDescuentos);
      }
      const _values = {
        ...values,
        padrones: values.padrones.map((p) => ({
          cargos: p.cargos,
          padron_id: p.padron_id,
          tipo_de_padron: p.tipo_de_padron,
          observaciones: p.observaciones,
          importe: p.importe,
          importe_sin_redondeo: p.importe_sin_redondeo,
          referencia_de_pago: p.referencia_de_pago,
          descuentos: p.descuentos,
        })),
      };

      const _recibos = await postRecibo(_values);
      setLoading(false);
      if (!_recibos.length) {
        clear(!activePublicoGeneral);
      }
    }, 500);
  };
  const metodoDescription = metodosPago?.find((t) => t.id === selectedMetodo)?.concepto;
  const msgConfirmacion = (_dataPagos) => (
    message.info({
      content: (
        <>
          <Row justify="center">
            <strong style={{ marginRight: 3 }}>
              ¿Está seguro que tu metodo de pago (
              {metodoDescription?.toUpperCase()}
              ) es el correcto?
            </strong>
          </Row>
          <Row align="middle" justify="center" style={{ width: '100%' }}>
            <Button
              type="link"
              className="success"
              onClick={() => {
                debouncePostRecibos(_dataPagos);
                message.destroy();
              }}
            >
              <Text type="success">SI</Text>
            </Button>
            <Button
              onClick={() => {
                message.destroy();
                setLoading(false);
              }}
              type="link"
              className="danger"
            >
              NO
            </Button>
          </Row>
        </>
      ),
      duration: 0,
    })
  );

  const localPayment = async (_dataPagos) => {
    if (isValid(_dataPagos)) {
      setLoading(true);
      msgConfirmacion(_dataPagos);
    }
  };

  const payWithNetPay = async () => {
    if (turnOnMSI && msiRef.current === '') {
      message.warn('Seleccione a cuantos meses desea dividir su deuda.');
      return;
    }

    const { padrones: _padrones } = getLocalPaymentFormatedData([formPago.getFieldsValue()]);
    setNetPayLoading(true);
    const response = await venta({
      msi: msiRef.current,
      serialNumber: corte?.terminal_de_netpay?.serial,
      folioNumber: new Date().toISOString(),
      amount: total,
      traceability: {
        padrones: _padrones.map((p) => ({
          ...p,
          cargos: p.cargos.join(),
          descuentos: p.descuentos.join(),
        })),
        ciudadano: ciudadano.id,
        usuario: user.id,
        importe: total,
      },
    });
    if (response) {
      // HERE PATCH
      dispatch({
        type: SET_LAST_PAYMENT_DATA,
        payload: _padrones.map((p) => ({
          tipo_de_padron: p.tipo_de_padron,
          padron_id: p.padron_id,
          cargo: p.cargos[0],
        })),
      });
      netpayRef.current = setTimeout(() => setVisibleGetRecibos(true), 60000);
    } else {
      setNetPayLoading(false);
    }
  };

  const getReciboNetPay = async () => {
    setLoading(true);
    const _recibos = await getRecibosPorPadrones(lastPaymentData);
    if (_recibos.length) {
      message.info('Recibos encontrados');
      clearTimeout(netpayRef.current);
      netpayRef.current = null;
      setVisibleGetRecibos(false);
      setTimeout(() => history.push('/recibos-de-caja?from=caja'));
    } else {
      setLoading(false);
    }
  };

  const handlePay = async (_dataPagos = dataPagos) => (selectedMetodo === metodosDePagoMap.NETPAY
    ? payWithNetPay() : localPayment(_dataPagos));

  const _getDepositosEnGarantia = async (_metodo) => {
    if (_metodo === metodosDePagoMap.DEPOSITO) {
      const results = await getDepositosEnGarantia({
        registro_de_beneficiario__ciudadano: ciudadano.id,
        estatus_de_deposito: 1,
      });
      if (results.filter((e) => e?.recibo_de_ingreso).length) {
        const _ttl = results.reduce((accum, curr) => accum + curr.importe_de_deposito, 0);
        setSaldoDisponibleEnDeposito(_ttl || 0);
      } else {
        setSaldoDisponibleEnDeposito(0);
      }
    }
    await formPago.validateFields();
  };

  const CardFormasDePago = () => (mostrarCobro ? (
    <Col xs={24} sm={24} md={6}>
      <Card
        footer={null}
        className="customCard"
        title={(
          <Row justify="space-between" style={{ height: 30 }}>
            <Text strong>
              Total a Pagar
            </Text>
            <Text strong>
              {currencyFormater(total)}
            </Text>
          </Row>
        )}
        style={{ height: 'auto' }}
      >
        <Row style={{ wordBreak: 'break-all' }}>
          {(!!optionsMSI.length && selectedMetodo === metodosDePagoMap.NETPAY && !PROD) && (
            <>
              <Col style={{ textAlign: 'center' }}>
                <Checkbox
                  onChange={({ target: { checked } }) => {
                    setTurnOnMSI(checked);
                    if (!checked) {
                      msiRef.current = '';
                    }
                  }}
                  style={{ marginBottom: 5 }}
                  checked={turnOnMSI}
                >
                  <Text>
                    ¿Desea pagar a meses sin intereses?
                  </Text>
                </Checkbox>
              </Col>
              <Col>
                {turnOnMSI && (
                  <Row justify="center" style={{ textAlign: 'center' }}>
                    <Col span={24}>
                      <Text>
                        ¿A cuantos meses desea dividir su deuda?
                      </Text>
                    </Col>
                    <Col span={24}>
                      <Radio.Group
                        options={optionsMSI}
                        optionType="button"
                        size="small"
                        onChange={({ target: { value } }) => {
                          msiRef.current = value;
                        }}
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </>
          )}
          <Button
            onClick={() => {
              if (permission.cobrar) {
                if (!solo_pago_netpay && selectedMetodo !== metodosDePagoMap.NETPAY) {
                  const copydataPagos = [...dataPagos, formPago.getFieldsValue()];
                  const willBeValid = isValid(copydataPagos);
                  if (willBeValid) {
                    requireDatosBancarios(true);
                  }
                } else {
                  payWithNetPay();
                }
              } else {
                message.warn('No cuenta con los permisos para pagar');
              }
            }}
            disabled={!corte || cambio < 0 || pinPadron?.hasBloqueoAlertas
              || loading || !somethingToPay}
            // Agregar de nuevo a reglas cuando se acepten pagos multiples con netpay
            // || (selectedMetodo === metodosDePagoMap.NETPAY && !socketsConnected)
            id="pay-button"
            style={{ margin: 0 }}
          >
            <Row justify="center">
              <Col>
                Pagar
              </Col>
              <Col style={{ marginLeft: 6 }}>
                {currencyFormater(total)}
              </Col>
              {(!solo_pago_netpay && selectedMetodo !== metodosDePagoMap.NETPAY) && (
                <Col span={24}>
                  {cambio < 0
                    ? `Falta ${currencyFormater(-cambio)}`
                    : `Cambio ${currencyFormater(cambio)}`}
                </Col>
              )}
              <Col span={24} className="btn-txt-caja">
                <Text>
                  {alerta}
                </Text>
              </Col>
            </Row>
          </Button>
        </Row>
        {selectedMetodo === metodosDePagoMap.DEPOSITO && (
          <Row justify="center">
            <Col span={24}>
              <Tooltip title="SALDO DISPONIBLE POR DEPOSITO EN GARANTIA">
                <Text>
                  {`SALDO DISPONIBLE: ${currencyFormater(saldoDisponibleEnDeposito || 0)}`}
                </Text>
              </Tooltip>
            </Col>
          </Row>
        )}
        <Row justify="end">
          <Col xs={2} sm={2} md={4}>
            <Button
              disabled={isButtonDisabled()}
              onClick={() => requireDatosBancarios()}
              size="large"
              className="stronger btn-pays"
              icon={solo_pago_netpay && dataPagos[0] ? <EditOutlined /> : <PlusOutlined />}
            />
          </Col>
        </Row>
        <Col span={23}>
          <Form
            layout="vertical"
            form={formPago}
            onFinish={cambio >= 0 ? null : () => requireDatosBancarios()}
          >
            <Col xs={22} sm={22} md={20}>
              <Form.Item
                name="metodo"
                rules={rules.required}
                label="Método"
              >
                <Select
                  disabled={!ciudadano || !somethingToPay || total === 0}
                  dataSource={metodosPago}
                  onChange={(metodo) => {
                    if (solo_pago_netpay || selectedMetodo === metodosDePagoMap.NETPAY) {
                      setDataPagos([]);
                    }
                    if (metodo === metodosDePagoMap.NETPAY) {
                      setDataPagos([{
                        metodo,
                        key: 1,
                        importe: total,
                      }]);
                      formPago.setFieldsValue({ importe: total });
                    } else {
                      formPago.setFieldsValue({ importe: getFaltante() });
                    }
                    _getDepositosEnGarantia(metodo);
                  }}
                  labelProp="concepto"
                  showArrow
                  allowClear={false}
                />
              </Form.Item>
            </Col>
            <Col xs={22} sm={22} md={20}>
              <Form.Item
                name="importe"
                label="Importe"
                rules={rules.importe}
                normalize={(val) => decimalNormalizer(val)}
              >
                <Input
                  disabled={!ciudadano || !somethingToPay || solo_pago_netpay || total === 0}
                  ref={inputImporteRef}
                  onChange={onChangeImporte}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Col>
        <Col span={23}>
          {!(dataPagos[0]?.metodo === metodosDePagoMap.NETPAY || solo_pago_netpay) && (
            <Form className="without-feedback" layout="vertical">
              {dataPagos.map((e) => (
                <Col span={24} key={e.key}>
                  <Row gutter={10}>
                    <Col span={20}>
                      <Form.Item>
                        <Input
                          disabled
                          value={metodosPago.find((item) => item.id === e.metodo)?.concepto}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                          const record = dataPagos.find((item) => item.key === e.key);
                          formPago.setFieldsValue({ ...formatReceived(record) });
                          setEditedPago(e);
                          setDataPagos(dataPagos.filter((item) => item.key !== e.key));
                          focusInputImporte();
                        }}
                      />
                    </Col>
                    <Col span={20}>
                      <Form.Item>
                        <Input disabled value={currencyFormater(e.importe)} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          const clone = dataPagos.filter((item) => item.key !== e.key);
                          const filtered = clone.map((item, key) => ({ ...item, key }));
                          setDataPagos(filtered);
                          onChangeDataPagos(filtered);
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
              ))}
            </Form>
          )}
        </Col>
        {(selectedMetodo === 1 && ENV_NAME === 'juarez') && (
          <Tooltip
            title="Click aqui si deseas contabilizar el monto total recibido en efectivo"
            overlayInnerStyle={{ fontWeight: 'bold' }}
            color={theme.primaryText}
          >
            <Col span={24}>
              <br />
              <Button
                onClick={setVisibleCalc}
                className="caja-action-button"
                disabled={!somethingToPay}
              >
                Calculadora de Montos
              </Button>
            </Col>
          </Tooltip>
        )}
        {permission.imprimir_orden_pago && (
          <Col span={24}>
            <br />
            <Button
              onClick={() => {
                if (VENTANILLA) {
                  setVisibleForm(true);
                } else {
                  imprimirOrdenDePago();
                }
              }}
              className="caja-action-button"
              disabled={!pinPadron?.key || total === 0 || pinPadron?.hasBloqueoAlertas
                || pinPadron.disabledOrdenPago}
            >
              Imprimir Orden de Pago
            </Button>
          </Col>
        )}
        {permission.imprimir_orden_pago && ENV_NAME === 'nayarit' && (
          <Col span={24}>
            <br />
            <Button
              onClick={() => {
                if (VENTANILLA) {
                  setVisibleForm(true);
                  setIsNormalReference(false);
                } else {
                  imprimirOrdenDePago({}, false);
                }
              }}
              className="caja-action-button"
              disabled={!pinPadron?.key || total === 0}
            >
              Transferencia Bancaria
            </Button>
          </Col>
        )}
        {ENV_NAME === 'juarez' && (
          <Col span={24}>
            <Button
              onClick={imprimirEstadoDeCuenta}
              className="caja-action-button"
              disabled={pinPadron.tipo_de_padron !== 3}
            >
              Imprimir Estado de Cuenta
            </Button>
          </Col>
        )}
        {!!ciudadano && (
          <>
            <Row justify="start" gutter={[10, 10]}>
              <Button
                size="large"
                type="link"
                onClick={() => setVerMas(!verMas)}
                icon={!verMas ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                style={{ padding: 6.4 }}
              >
                {screen.md && !verMas ? 'Ver más' : 'Ver menos'}
              </Button>
            </Row>
            {verMas && (<InfoToSeeMore />)}
          </>
        )}
      </Card>
    </Col>
  ) : null);

  const recuperarRecibosTitle = (
    <Typography.Text type="warning">
      Solo usar en caso de finalizar la transacción
      (mensaje &quot;Recibo generado correctamente&quot; visible en terminal)
      y no haber sido redirigido al recibo.
    </Typography.Text>
  );

  const NetPayLoading = () => (
    <Row gutter={[10, 10]}>
      <Col span={24}>
        Esperando respuesta de terminal
      </Col>
      {visibleGetRecibos && (
        <Col span={24}>
          <Button onClick={getReciboNetPay}>
            <Tooltip title={recuperarRecibosTitle}>
              Recuperar recibos
              {' '}
              <QuestionCircleOutlined />
            </Tooltip>
          </Button>
        </Col>
      )}
      <Col span={24}>
        <Button onClick={() => {
          setNetPayLoading(false);
          clearTimeout(netpayRef.current);
          netpayRef.current = null;
          setVisibleGetRecibos(false);
        }}
        >
          Cancelar
        </Button>
      </Col>
    </Row>
  );

  const hasActionPermissionPadron = (action) => hasActionPermission(
    tiposDePadrones.find((p) => p.id === tipoDePadronSelected)?.model,
    action,
  );

  const allowAddPadron = () => hasActionPermissionPadron(ActionsNames.add);
  const allowEditPadron = () => hasActionPermissionPadron(ActionsNames.change);

  const items = [
    permission.imprimir_orden_pago && {
      disabled: !pinPadron || pinPadron?.hasBloqueoAlertas,
      onClick: () => {
        if (VENTANILLA) {
          setVisibleForm(true);
          setIsNormalReference(true);
        } else {
          imprimirOrdenDePago();
        }
      },
      label: 'Estado de cuenta desglosado',
    },
    permission.puede_ver_orden_catastro && {
      disabled: !pinPadron || pinPadron?.hasBloqueoAlertas,
      onClick: () => {
        if (VENTANILLA) {
          setVisibleForm(true);
          setIsNormalReference(true);
        } else {
          imprimirOrdenDePago({ otro_formato: true });
        }
      },
      label: 'Estado de cuenta en 0',
    },
    permission.puede_ver_orden_catastro && {
      disabled: !pinPadron || pinPadron?.hasBloqueoAlertas,
      onClick: () => {
        if (VENTANILLA) {
          setVisibleForm(true);
          setIsNormalReference(true);
        } else {
          imprimirOrdenDePago({ orden_anual: true });
        }
      },
      label: 'Estado de cuenta concentrado por año',
    },
    permission.imprimir_orden_pago_transferencia && {
      disabled: !pinPadron || pinPadron?.hasBloqueoAlertas,
      onClick: () => {
        if (VENTANILLA) {
          setVisibleForm(true);
          setIsNormalReference(false);
        } else {
          imprimirOrdenDePago({}, false);
        }
      },
      label: 'Transferencia Bancaria',
    },
  ].filter((e) => e);

  return (
    <ThemeProvider theme={theme || defSigob}>
      <GlobalStyles />
      <Spin
        tip={netPayLoading ? <NetPayLoading /> : 'Cargando...'}
        spinning={loading || netPayLoading}
        style={{ height: '100vh', width: '100vw' }}
      >
        <Card
          footer={null}
          className="customCard cardCaja"
          style={{ height: '100vh', overflow: 'scroll' }}
        >
          <Row align="center" justify="center" className="form-Container">
            <Col span={24}>
              <Row gutter={[5, 10]}>
                <Col xs={24} sm={24} md={mostrarCobro ? 18 : 24}>
                  <Card
                    footer={null}
                    style={{ borderBottom: '50vh' }}
                    title={(
                      <>
                        <Row justify="space-between" gutter={[10, 10]}>
                          <Col>
                            <Button
                              type="link"
                              size="large"
                              onClick={() => history.push('/dashboard')}
                              icon={<ArrowLeftOutlined />}
                            >
                              Salir de la
                              {' '}
                              {mostrarCobro ? 'Caja' : 'Ventanilla'}
                            </Button>
                          </Col>
                          <ButtonPublicoGeneral />
                          {(!mostrarCobro && permission.generar_convenio) && (
                            <Col>
                              {!(pinPadron?.key && selectedRowKeys[pinPadron?.key]?.length) ? (
                                <Tooltip
                                  title={(
                                    <span>
                                      Para poder habilitar el botón de convenio necesita seleccionar
                                      cargos de un padrón y tener marcado el icono
                                      {' '}
                                      <PushpinFilled />
                                      {' '}
                                      del padron a cual desea crear el convenio.
                                    </span>
                                  )}
                                >
                                  <Button
                                    disabled
                                    icon={<FileDoneOutlined />}
                                    size="large"
                                    style={{ borderRadius: 4 }}
                                  >
                                    {screen.md && 'Convenio'}
                                  </Button>
                                </Tooltip>
                              ) : (
                                <Button
                                  onClick={generarConvenio}
                                  icon={<FileDoneOutlined className="success" />}
                                  size="large"
                                  style={{ borderRadius: 4 }}
                                >
                                  {screen.md && 'Convenio'}
                                </Button>
                              )}
                            </Col>
                          )}
                          {(permission?.acreditar_recibo) && (
                            <ModalAcreditacion
                              pinPadron={pinPadron}
                              disabled={!pinPadron?.key
                              || !selectedRowKeys[pinPadron?.key]?.length}
                            />
                          )}
                          <Col>
                            <Button
                              onClick={() => clear(!activePublicoGeneral)}
                              icon={<DeleteOutlined />}
                              size="large"
                              type="danger"
                              style={{ borderRadius: 4 }}
                              className="no-color"
                            >
                              {screen.md && 'Limpiar Búsqueda'}
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}
                  >
                    <Form
                      name="ciudadano"
                      form={formCiudadano}
                      layout="vertical"
                      className="without-feedback"
                    >
                      <Col span={24}>
                        <InputSearch
                          ref={inputCiudadanoRef}
                          placeholder="Búsqueda por Nombre, CURP o Clave Ciudadana"
                          onPressEnter={searchCiudadano}
                          disabled={!!ciudadano || !permission.hasAllViewPermission}
                          loading={loading}
                          control={(
                            <BusquedaAvanzada
                              dataExterno={ciudadanoList}
                              visible={visibleBusqueda}
                              setVisible={(_visible) => {
                                setVisibleBusqueda(_visible);
                                formCiudadano.resetFields();
                              }}
                              callback={getCargosAPagar}
                              setDataExterno={setCiudadanosList}
                              disabled={!!ciudadano || !permission.hasAllViewPermission}
                              msgAsignarEntidad={msgAsignarEntidad}
                            />
                          )}
                        />
                      </Col>
                    </Form>
                    <Form
                      name="padron"
                      layout="vertical"
                      className="without-feedback form-busqueda-caja"
                      form={formPadron}
                      onFinish={tipoDePadronSelected <= 0 && !loading
                        ? handleSearchFolio : searchPadron}
                      style={{
                        ...theme,
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                        marginTop: -15,
                        paddingTop: 15,
                      }}
                    >
                      <Row style={{ display: 'flex', padding: '0 5px' }} gutter={5}>
                        <Col span={!mostrarCobro ? 18 : 20}>
                          <Form.Item style={{ padding: '2px 5px' }}>
                            <Input.Group
                              style={{ display: 'flex' }}
                              compact
                            >
                              <Form.Item name="q" normalize={capitalize} style={{ width: 'calc(100% - 64px)', marginBottom: 0 }}>
                                <Input
                                  placeholder="Búsqueda de padrón"
                                  // onPressEnter={tipoDePadronSelected <= 0
                                  //   ? handleSearchFolio : searchPadron}
                                  allowClear
                                  disabled={
                                    VENTANILLA
                                    || !ciudadano
                                    || !permission.hasAllViewPermission
                                  }
                                  className="input-busqueda-caja busqueda-padron"
                                  autoComplete="off"
                                  addonBefore={(
                                    <Select
                                      onChange={setTipoDePadronSelected}
                                      className="select-before"
                                      style={{ width: 150 }}
                                      defaultValue={tipoDePadronSelected}
                                      disabled={!ciudadano}
                                      showArrow={!ciudadano}
                                      dataSource={tiposDePadrones.filter((e) => e.id !== 5)}
                                      allowClear={false}
                                    />
                                  )}
                                />
                              </Form.Item>
                              <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                  style={{
                                    marginTop: 0,
                                    width: !screen.xs && 64,
                                    height: !screen.xs && 64,
                                  }}
                                  disabled={!ciudadano || !permission.hasAllViewPermission}
                                  className="primary-on-hover"
                                  icon={<SearchOutlined />}
                                  htmlType="submit"
                                />
                              </Form.Item>
                              {hasAdvanceSearchPermissions(tipoDePadronSelected) && (
                                <Form.Item style={{ marginBottom: 0 }}>
                                  <Button
                                    onClick={() => setVisibleBusquedaPadrones(true)}
                                    disabled={!ciudadano}
                                    style={{
                                      ...theme,
                                      width: !screen.xs && 64,
                                      height: !screen.xs && 64,
                                      marginTop: 0,
                                    }}
                                    className="primary-on-hover"
                                  >
                                    ...
                                  </Button>
                                </Form.Item>
                              )}
                            </Input.Group>
                          </Form.Item>
                        </Col>
                        {(!!items.length && !mostrarCobro) && (
                          <Col span={!mostrarCobro ? 6 : 4}>
                            <Dropdown menu={{ items }}>
                              <Button
                                block
                                style={{ height: !screen.xs && 64 }}
                                disabled={!pinPadron}
                                className="caja-action-button"
                              >
                                <Space>
                                  <b>Imprimir</b>
                                  <PrinterFilled />
                                  <DownOutlined />
                                </Space>
                              </Button>
                            </Dropdown>
                          </Col>
                        )}
                      </Row>
                    </Form>
                    <Col span={24} className="collapse-wrapper">
                      {padrones.map((padron) => (
                        <PanelPadron
                          key={padron.key}
                          padron={padron}
                          canAddCargos={permission.generar_cargos}
                          pinPadron={pinPadron}
                          setPinPadron={setPinPadron}
                          mostrarCobro={mostrarCobro}
                          cargos={padron.cargos}
                          selectedRowKeys={selectedRowKeys[padron.key]}
                        />
                      ))}
                    </Col>
                  </Card>
                </Col>
                <CardFormasDePago />
              </Row>
            </Col>
          </Row>
        </Card>
        <Footer editable={false} />
      </Spin>
      <Calculadora
        visible={visibleCalc}
        setVisible={setVisibleCalc}
        callback={(values) => {
          formPago.setFieldsValue({ importe: values });
          onChangeImporte({
            target: {
              selectionStart: values.toString().length,
              value: values,
            },
          });
        }}
      />
      <ModalAgregarCargos
        callback={(_padron) => {
          dispatch({
            type: SET_PADRON_REQUIERE_FETCH,
            payload: _padron.key,
          });
        }}
        visible={!!pinPadronAgregarCargos}
        padron={pinPadronAgregarCargos}
        onCancel={() => {
          dispatch({ type: SET_PIN_PADRON_AGREGAR_CARGOS });
        }}
      />
      <ModalInfoPadron unidadesRecaudadoras={unidadesRecaudadoras} />
      <BusquedaAvanzadaPadrones
        visible={visibleBusquedaPadrones}
        setVisible={setVisibleBusquedaPadrones}
        callback={(padron) => {
          if (Array.isArray(padron) && tipoDePadronSelected === 19) {
            addMultiplesPadrones(padron);
          } else {
            addPadron(padron.id);
          }
        }}
        dataExterno={padronesList}
        setDataExterno={setPadronesList}
        tipoDePadron={tipoDePadronSelected}
        msgAsignarEntidad={msgAsignarEntidad}
        inputQ={searchQPadron}
        setInputQ={setSearchQPadron}
        allowAdd={allowAddPadron()}
        showMultipleSelection
        allowEdit={allowEditPadron()}
        visibleRFC={false}
        visibleCURP={false}
        allowAddAddress={permission.registrar_direccion}
      />
      <Previewer
        visible={visiblePreview}
        setVisible={setVisiblePreview}
        base64={base64}
        allowDownload
        customFileNameFormat={() => {
          const fileName = getFileName({
            data: {
              padron: pinPadron,
              folio: folioOrdenDePago,
            },
            tipoDeDocumento: TIPOS_DE_DOCUMENTO_ENUM.ORDEN_DE_PAGO,
          });
          return fileName;
        }}
      />
      <ModalAlertas />
      <ModalPadronesAConsultarAdeudo
        visible={visibleModalConsulta}
        onCancel={() => setVisibleModalConsulta(false)}
      />
      <ModalObservaciones
        callback={(values) => {
          pinPadron.observaciones = values;
        }}
      />
      {mostrarCobro && (
        <>
          <ModalSockets />
          <DatosDePago
            selectedMetodo={selectedMetodo}
            visible={visibleDatosDePago}
            callback={agregarDatoDePago}
            justPay={justPay}
            secondCallback={(values) => agregarDatoDePago(values, true)}
            isEditing={editedPago}
            onCancel={() => {
              setEditedPago();
              setVisibleDatosDePago(false);
            }}
            editedPago={editedPago}
            bancos={bancos}
          />
        </>
      )}
      {!mostrarCobro && permission.generar_convenio && (
        <ModalConvenio
          hasPermission={permission.generar_convenio}
          visible={visibleConvenio}
          setVisible={setVisibleConvenio}
          tipoDePadron={pinPadron?.tipo_de_padron}
          padron={pinPadron}
          resetCaja={() => clear(!activePublicoGeneral)}
          cargos={padrones.find((e) => e.key === pinPadron?.key)?.cargos
            .filter((c) => selectedRowKeys[pinPadron.key].includes(c.id))}
        />
      )}
      <ModalExtraInfoPaymentOrder
        visible={visibleForm}
        setVisible={setVisibleForm}
        callback={(values) => imprimirOrdenDePago(values, isNormalReference)}
      />
      <ModalCorteControls />
      <ModalCobroParcialidades
        permission={permission}
        callback={(key) => {
          dispatch({
            type: SET_PADRON_REQUIERE_FETCH,
            payload: key,
          });
        }}
      />
      <ModalReciboConvenio />
    </ThemeProvider>
  );
};

Caja.propTypes = {
  mostrarCobro: PropTypes.bool,
  permission: PropTypes.shape({
    cobrar: PropTypes.bool,
    hasAllViewPermission: PropTypes.bool,
    cobrar_publico_general: PropTypes.bool,
    puede_ver_orden_catastro: PropTypes.bool,
    imprimir_orden_pago: PropTypes.bool,
    imprimir_opinion_cumplimiento: PropTypes.bool,
    imprimir_constancia_situacion_fiscal: PropTypes.bool,
    generar_cargos: PropTypes.bool,
    registrar_direccion: PropTypes.bool,
    generar_convenio: PropTypes.bool,
    imprimir_orden_pago_transferencia: PropTypes.bool,
    acreditar_recibo: PropTypes.bool,
  }).isRequired,
};

Caja.defaultProps = {
  mostrarCobro: true,
};

export default Caja;
